import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import moment from 'moment';
import propTypes from 'prop-types';
import cn from 'classnames';
import { setTracker } from '~/Redux/Modules/DayTrackers';
import { createHabitTracked, deleteHabitTracked } from '~/Redux/Modules/HabitTrackeds/actions';
import FilledWater from '~/Components/Global/Assets/Svgs/FilledWater';
import habitIcons from './HabitIconsLibrary';
import './HabitDayTracking.scss';

const HabitDayTracking = ({ habits, start, trackedHabits, user, day, setTracker }) => {
  const [localHabits, setHabits] = useState([]);
  const [localTrackedHabits, setLocalTrackedHabits] = useState({});
  const dispatch = useDispatch();

  const handleToggleTracker = opt => {
    setTracker(opt);
  };

  useEffect(() => {
    let isMounted = true;

    const sortedHabits = [...habits].sort((a, b) => {
      if (a.priority < b.priority) return -1;
      if (a.priority > b.priority) return 1;
      return 0;
    });

    const filteredHabits = sortedHabits.filter(habit => {
      const habitStart = moment(habit.start);
      const habitEnd = habit.end ? moment(habit.end) : null;

      const habitStartBeforeOrAtDate = habitStart.isSameOrBefore(start, 'minute');
      const habitNotAfterDate = !habitStart.isAfter(start);

      const habitEndAfterOrNotBeforeDate =
        !habitEnd || habitEnd.isAfter(start) || !habitEnd.isBefore(start);

      return habitStartBeforeOrAtDate && habitNotAfterDate && habitEndAfterOrNotBeforeDate;
    });

    if (isMounted) {
      setHabits(filteredHabits);
    }

    const cleanup = () => {
      isMounted = false;
    };
    return cleanup;
  }, [habits, start]);

  useEffect(() => {
    const habitsTrackedData = trackedHabits.reduce((acc, trackedHabit) => {
      const habitId = trackedHabit.habit_id;
      const trackDate = moment(trackedHabit.track_date).format('YYYY-MM-DD');

      if (!acc[habitId]) {
        acc[habitId] = {};
      }

      acc[habitId][trackDate] = trackedHabit;
      return acc;
    }, {});

    setLocalTrackedHabits(habitsTrackedData);
  }, [trackedHabits]);

  const isPastOrToday = (date, timezone) => {
    const now = moment().utcOffset(timezone, true);
    const targetDate = moment(date).utcOffset(timezone, true);

    // Set the time of both the target date and now to the end of the day
    targetDate.endOf('day');
    now.endOf('day');

    return targetDate.isSameOrBefore(now);
  };

  const getTrackedHabit = (date, habit) => {
    const formattedDate = moment(date).format('YYYY-MM-DD HH:mm:ss'); // Include time information

    return trackedHabits.find(
      trackedHabit =>
        trackedHabit.track_date === formattedDate && trackedHabit.habit_id === habit.id
    );
  };

  const handleTrackHabit = (date, habit) => {
    const startOfDay = moment(date).startOf('day');
    const formattedDate = startOfDay.format('YYYY-MM-DD HH:mm:ss');

    const trackedHabit = getTrackedHabit(formattedDate, habit);

    if (trackedHabit) {
      dispatch(deleteHabitTracked(trackedHabit));
    } else {
      const newHabitTracked = {
        id: 'temp-id',
        track_date: formattedDate,
        userId: user.id,
        habit_id: habit.id,
      };
      dispatch(createHabitTracked(newHabitTracked));
    }
  };

  return (
    <div className="habit-day-tracking">
      <div className="habit-day-tracking-title">
        <h6 className="habit-day-tracking-header">HABITS</h6>
        <span className="toggle-tracker" onClick={() => handleToggleTracker('water')}>
          <FilledWater fill="#BDBDBD" stroke="#BDBDBD" />
        </span>
      </div>
      <div
        className={cn('habit-day-column', {
          'single-habit-grid': localHabits.length === 1,
          'double-habit-grid': localHabits.length === 2,
        })}
      >
        {localHabits.map((habit, key) => {
          const startDate = moment(start);
          const currentDate = moment(startDate).add(day - 1, 'days');
          const trackDate = currentDate.format('YYYY-MM-DD');
          const isHabitTracked = habit && localTrackedHabits[habit.id]?.[trackDate];
          const timeZone = start.utcOffset();
          const isDayPastOrToday = isPastOrToday(currentDate, timeZone);
          return (
            <div
              className="habit-day-row"
              onClick={() => {
                if (isDayPastOrToday) {
                  handleTrackHabit(currentDate, habit);
                }
              }}
            >
              <div className="habit-day-icon" key={key}>
                {React.cloneElement(habitIcons[habit.icon], {
                  fill: isHabitTracked ? habit.color : '#D0D5DD',
                })}
              </div>
              <div className="habit-day-title">{habit.title}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

HabitDayTracking.propTypes = {
  habits: propTypes.array,
  start: propTypes.object,
  trackedHabits: propTypes.array,
  user: propTypes.oneOfType([propTypes.bool, propTypes.object]),
  day: propTypes.string,
  setTracker: propTypes.func,
};

const mapStateToProps = ({ DateRange, Habits, User, HabitTrackeds }) => {
  return {
    habits: Habits.habitCollection.habits,
    start: DateRange.start,
    day: DateRange.start.toString(),
    trackedHabits: HabitTrackeds.habitTrackedCollection.habitsTracked,
    user: User.data,
  };
};

const mapDispatchToProps = dispatch => ({
  setTracker: opt => dispatch(setTracker(opt)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HabitDayTracking);
